
import uniqueId from "app/helpers/unique_id"
import trans from "trans"
import CircleShape from "./types/CircleShape"
import BrushShape from "./types/BrushShape"
import RectangleShape from "./types/RectangleShape"
import FrameShape from "./types/FrameShape"
import TriangleShape from "./types/TriangleShape"
import BitmapShape from "./types/BitmapShape"
import BadgeShape from "./types/BadgeShape"
import TextShape from "./types/TextShape"
import DynamicTextShape from "./types/DynamicTextShape"
import CustomSetShape from "./types/CustomSetShape";
import TextWithElementsShape from "./types/TextWithElementsShape";
import SourceImageShape from "./types/SourceImageShape";
import { getLastUsedFont, SOURCE_IMAGE_INDEX } from "../shape-edit/forms/helpers";
import PathShape from "./types/PathShape";
import PolygonShape from "./types/PolygonShape";
import CurvedTextShape from "./types/CurvedTextShape";
import LineShape from "./types/LineShape";
import CircleTextShape from "./types/CircleTextShape";

import {
    BADGE_TYPE, CIRCLE_TYPE, CUSTOM_SET_TYPE, DYNAMIC_TEXT_TYPE, SOURCE_IMAGE_TYPE, TEXT_TYPE, TEXT_WITH_ELEMENTS_TYPE,
    CIRCLE_TEXT_TYPE, RECTANGLE_TYPE, TRIANGLE1_TYPE, TRIANGLE2_TYPE, DASHED_FRAME_TYPE, POLYGON_TYPE, BEZIER_TYPE,
    CURVED_TEXT_TYPE, LINE_TYPE, IMAGE_FROM_ELEMENTS_TYPE, BITMAP_TYPE, TEXT_INSIDE_SHAPE_TYPE, FRAME_TYPE, BRUSH_TYPE,
    EXTERNAL_IMAGE_SOURCE_TYPE,
    RATING_TYPE
} from './types/shape';
import ImageFromElementsShape from "./types/ImageFromElementsShape"
import TextInsideShape from "./types/TextInsideShape"
import { SizeType } from "app/types/model-types"
import ExternalImageShape from "./types/ExternalImageShape"
import { Theme } from "@bdfx/mergado-ui-kit"
import RatingShape from "./types/Rating"
import { getRatingShapeSize } from "template-edit/components/helper"

const { min, max, abs, sin, cos, PI, round, atan2 } = Math

export const defaultFontName = 'Roboto'
export const XMLHeader = '<?xml version="1.0" encoding="UTF-8" standalone="no"?>'

export function getCircle(format: any, priority: number = 1): CircleShape {
    const fontSize = round(format.width / 50) * 2
    const radius: number = round(format.width / 30) * 2
    const fontFamily = getLastUsedFont()
    return {
        index: uniqueId(),
        type: CIRCLE_TYPE,
        text_content: "Text",
        text_fontsize: fontSize,
        text_fontfamily: fontFamily,
        text_color: "#BED6F2",
        width: 2 * radius,
        height: 2 * radius,
        color: "#123655",
        is_draggable: true,
        is_aspect_size_ratio: true,
        name: "",
        priority: priority,
        x: round(radius + (fontSize / 2)),
        y: round(radius + (fontSize / 2)),
    }
}

export function getCircleText(format: SizeType, priority: number = 1): CircleTextShape {
    const side = min(format.width, format.height)
    const fontSize = round(side / 30)
    const radius: number = round(side / 10)
    return {
        index: uniqueId(),
        type: CIRCLE_TEXT_TYPE,
        text_direction: 0,
        text_content: "Text ★ abcde",
        text_fontsize: fontSize,
        text_fontfamily: 'DejaVu-Sans-Mono-Bold',
        text_color: '#000000',
        stroke_width: round(.1 * radius),
        stroke_color: '#66b7ca',
        color: '#80cfde',
        letter_spacing: round(fontSize / 6),
        width: 2 * radius,
        height: 2 * radius,
        is_draggable: true,
        is_aspect_size_ratio: true,
        name: "",
        priority: priority,
        x: round(format.width - 2.5 * radius),
        y: round(format.height - 2.5 * radius),
    }
}

export function getBrush(format: SizeType, priority: number = 1): BrushShape {
    return {
        index: uniqueId(),
        type: BRUSH_TYPE,
        width: 1,
        height: 1,
        x: format.width / 2,
        y: format.height / 2,
        is_draggable: true,
        is_aspect_size_ratio: false,
        priority: priority,
    }
}

export function getRectangle(format: SizeType, priority: number = 1): RectangleShape {
    const fontSize = round(format.width / 50) * 2
    const width: number = round(format.width / 5)
    const height: number = round(width / 3)
    const posX: number = round(format.width - (width / 2))
    const posY: number = round(height + (fontSize / 2))
    const fontFamily = getLastUsedFont()
    return {
        index: uniqueId(),
        type: RECTANGLE_TYPE,
        text_content: "Text",
        text_fontsize: fontSize,
        text_fontfamily: fontFamily,
        text_color: "#43739D",
        color: "#97B5DA",
        width: width,
        height: height,
        is_draggable: true,
        is_aspect_size_ratio: false,
        scale: 1,
        name: "",
        x: posX,
        y: posY,
        priority: priority,
        corner_radius: null,
    }
}

export function getRating(format: SizeType, priority: number = 1): RatingShape {
    const fontSize = round(format.width * 3.2 / 50)

    const newShape: RatingShape = {
        index: uniqueId(),
        type: RATING_TYPE,
        is_dynamic: 1,
        rating_xml_element: 'BFE_S_H_PRODUCT_RATING',
        rating_count_xml_element: 'BFE_S_H_PRODUCT_RATING_COUNT',
        rating_from_depots: 0,
        rating_max_value: 100,
        rating_min_stars: 3,
        text_fontsize: fontSize,
        text_fontfamily: 'DejaVu-Sans-Condensed-Bold',
        text_color: "#343434",
        color: "#ffe007",
        stroke_width: 2,
        star_symbol: 2,
        scale: 1,
        name: "",
        is_draggable: true,
        priority: priority,
        width: null,
        height: null,
        x: null,
        y: null,
    }

    const ns = getRatingShapeSize(fontSize, newShape.text_padding, newShape.stroke_width)

    const posX: number = round(format.width / 2)
    const posY: number = round(format.height - .8 * ns.height)

    return {
        ...newShape,
        width: ns.width,
        height: ns.height,
        x: posX,
        y: posY,
    }
}

export function getTriangle(format: SizeType, type: "triangle1" | "triangle2", priority: number = 1): TriangleShape {
    const fontSize = round(format.width / 50) * 2
    const diameter: number = round(format.width / 5)
    const posX: number = (type === 'triangle2') ? (diameter / 2) + (fontSize / 2) : format.width - (diameter / 2) - (fontSize / 2)
    const posY: number = format.height - (diameter / 2) - (fontSize / 2)
    const fontFamily = getLastUsedFont()
    return {
        index: uniqueId(),
        type: type,
        text_content: "Text",
        text_fontsize: fontSize,
        text_fontfamily: fontFamily,
        text_color: "#ffffff",
        color: "#6496C4",
        stroke_width: 0,
        stroke_color: "#ffffff",
        name: "",
        width: diameter,
        x: posX,
        y: posY,
        is_draggable: true,
        is_aspect_size_ratio: true,
        priority: priority,
    }
}

export function getPolygon(format: SizeType): PolygonShape {
    return {
        index: uniqueId(),
        type: POLYGON_TYPE,
        color: "transparent",
        stroke_width: 10,
        stroke_color: "#425C94",
        name: "",
        width: 1,
        height: 1,
        x: format.width / 2,
        y: format.height / 2,
        is_draggable: true,
        is_aspect_size_ratio: true,
        priority: 1,
        points: null,
    }
}

export function getBezier(format: SizeType): PathShape {
    return {
        index: uniqueId(),
        type: BEZIER_TYPE,
        color: "transparent",
        stroke_width: 10,
        stroke_color: "#38538F",
        name: "",
        width: 1,
        height: 1,
        x: format.width / 2,
        y: format.height / 2,
        is_draggable: true,
        is_aspect_size_ratio: true,
        priority: 1,
        points: null,
    }
}

export function getCurvedText(format: SizeType): CurvedTextShape {
    const fontSize = round(format.width / 30) * 2
    const fontFamily = getLastUsedFont()
    return {
        index: uniqueId(),
        type: CURVED_TEXT_TYPE,
        color: "transparent",
        text_color: Theme.steelblue,
        stroke_color: Theme.blue,
        stroke_width: 0,
        name: "",
        width: 1,
        height: 1,
        x: format.width / 2,
        y: format.height / 2,
        text_content: "Text text",
        text_fontsize: fontSize,
        text_fontfamily: fontFamily,
        is_draggable: true,
        is_aspect_size_ratio: true,
        priority: 1,
        points: null,
    }
}

export function getTextInsideShape(format: SizeType): TextInsideShape {
    const fontSize = round(format.width / 20)
    const fontFamily = getLastUsedFont()

    return {
        index: uniqueId(),
        type: TEXT_INSIDE_SHAPE_TYPE,
        color: null,
        name: "",
        width: 1,
        height: 1,
        x: format.width / 2,
        y: format.height / 2,
        text_content: "",
        text_color: "#000000",
        text_fontsize: fontSize,
        text_fontfamily: fontFamily,
        is_draggable: true,
        is_dynamic: 0,
        is_aspect_size_ratio: true,
        text_wrap: true,
        letter_case: null,
        priority: 1,
        points: null,
        lines: null,
    }
}

export function getFrame(format: SizeType, dashed: boolean, priority: number = 1): FrameShape {
    const frame: FrameShape = {
        index: uniqueId(),
        type: FRAME_TYPE,
        color: "#000",
        name: "",
        width: 10,
        is_draggable: false,
        is_aspect_size_ratio: null,
        dash_density: 5,
        dash_length: 60,
        priority: priority,
        x: 0,
        y: 0,
        margin_x: 0,
        margin_y: 0,
    }

    if (dashed) {
        frame.type = "dashedFrame"
    }

    return frame
}

export function getBitMap(format: SizeType): BitmapShape {
    const size: number = 100
    const posX: number = (format.width / 2)
    const posY: number = (format.height / 2)

    return {
        index: uniqueId(),
        type: BITMAP_TYPE,
        name: '',
        color: "#d61717",
        imageBlob: '',
        file: '',
        x: posX,
        y: posY,
        scale: 1,
        width: size,
        height: size,
        is_draggable: true,
        is_aspect_size_ratio: true,
        priority: 1
    }
}

export function getSourceImage(): SourceImageShape {
    return {
        index: SOURCE_IMAGE_INDEX,
        type: SOURCE_IMAGE_TYPE,
        name: trans('source_image'),
        priority: 1,
        x: 0,
        y: 0,
        is_draggable: false,
        is_dynamic: 0,
    }
}

export function getBadge(format: SizeType): BadgeShape {
    const size: number = 100
    const posX: number = (format.width / 2)
    const posY: number = (format.height / 2)

    return {
        index: uniqueId(),
        type: BADGE_TYPE,
        name: "",
        color: "#d61717",
        imageBlob: "",
        file: '',
        x: posX,
        y: posY,
        scale: 1,
        width: size,
        height: size,
        is_draggable: true,
        is_aspect_size_ratio: true,
        priority: 1,
        mime: "",
        size: 0,
    }
}

export function getExternalImageSource(format: SizeType, priority: number = 1): ExternalImageShape {
    const width: number = round(format.width / 5)
    const height: number = round(width / 3)
    const posX: number = round(format.width / 2)
    const posY: number = round(format.height / 2)

    return {
        index: uniqueId(),
        type: EXTERNAL_IMAGE_SOURCE_TYPE,
        color: null,
        is_draggable: true,
        is_aspect_size_ratio: false,
        name: "",
        x: posX,
        y: posY,
        priority: priority,
        scale: 1,
        width: width,
        height: height,
        imageBlob: "",
        mime: "",
        size: 0,
    }
}

export function getText(format: SizeType, priority: number = 1): TextShape {
    const fontSize = round(format.width / 30) * 2
    const width: number = round(format.width / 5)
    const height: number = round(width / 3)
    const posX: number = round(format.width / 2)
    const posY: number = round(format.height / 2)
    const fontFamily = getLastUsedFont()

    return {
        index: uniqueId(),
        type: TEXT_TYPE,
        text_content: "Text",
        text_fontsize: fontSize,
        text_fontfamily: fontFamily,
        text_color: "#000",
        text_wrap: null,
        letter_case: null,
        text_overflow: null,
        text_decoration: null,
        text_decoration_color: null,
        text_align: null,
        text_valign: null,
        text_padding: null,
        color: null,
        is_draggable: true,
        is_aspect_size_ratio: false,
        name: "",
        x: posX,
        y: posY,
        priority: priority,
        scale: 1,
        width: width,
        height: height,
    }
}

export function getDynamicText(format: SizeType, priority: number = 1): DynamicTextShape {
    const fontSize = round(format.width / 30) * 2
    const posX: number = round(format.width / 2)
    const posY: number = round(format.height / 2)
    const fontFamily = getLastUsedFont()

    return {
        index: uniqueId(),
        type: DYNAMIC_TEXT_TYPE,
        name: "",
        width: round(format.width / 2),
        height: round(fontSize * 1.25),
        text_content: "Feed element",
        text_fontsize: fontSize,
        text_fontfamily: fontFamily,
        text_color: "#000",
        text_xml_element: '',
        text_wrap: null,
        letter_case: null,
        text_overflow: null,
        text_align: null,
        text_valign: null,
        text_padding: null,
        text_background_type: 'none',
        text_background_color: "#ffffff",
        is_draggable: true,
        is_dynamic: 1,
        is_aspect_size_ratio: false,
        x: posX,
        y: posY,
        priority: priority,
        scale: 1,
    }
}

export function getImageFromElements(format: SizeType, priority: number = 1): ImageFromElementsShape {
    const posX: number = round(format.width / 2)
    const posY: number = round(format.height / 2)

    return {
        index: uniqueId(),
        type: IMAGE_FROM_ELEMENTS_TYPE,
        name: "",
        width: round(format.width / 4),
        height: round(format.width / 4),
        image_xml_element: '',
        is_draggable: true,
        is_dynamic: 1,
        is_aspect_size_ratio: true,
        is_remove_background: null,
        x: posX,
        y: posY,
        priority: priority,
        scale: 1,
    }
}

export function getTextWithElements(format: SizeType, priority: number = 1): TextWithElementsShape {
    const fontSize = round(format.width / 30) * 2
    const posX: number = round(format.width / 2)
    const posY: number = round(format.height / 2)
    const fontFamily = getLastUsedFont()

    return {
        index: uniqueId(),
        type: TEXT_WITH_ELEMENTS_TYPE,
        name: "",
        width: round(format.width / 2),
        height: round(fontSize * 1.25),
        text_content: 'Text %ITEM_ID%',
        text_content_replaced: '',
        text_fontsize: fontSize,
        text_fontfamily: fontFamily,
        text_color: "#000",
        color: null,
        text_xml_element: '',
        text_wrap: null,
        letter_case: null,
        text_overflow: null,
        text_align: null,
        text_valign: null,
        text_padding: null,
        text_background_type: 'none',
        text_background_color: "#ffffff",
        is_draggable: true,
        is_dynamic: 1,
        is_aspect_size_ratio: false,
        x: posX,
        y: posY,
        scale: 1,
        priority: priority,
    }
}

export function getCustomSet(format: SizeType, priority: number = 1): CustomSetShape {
    const size: number = 100
    const posX: number = (format.width / 2)
    const posY: number = (format.height / 2)

    return {
        index: uniqueId(),
        type: CUSTOM_SET_TYPE,
        name: "",
        imageBlob: "",
        x: posX,
        y: posY,
        scale: 1,
        width: size,
        height: size,
        is_draggable: true,
        is_aspect_size_ratio: true,
        priority: 1
    }
}

export function getLine(format: SizeType, priority: number = 1): LineShape {
    const posX: number = (format.width / 2)
    const posY: number = (format.height / 2)
    return {
        index: uniqueId(),
        type: LINE_TYPE,
        stroke_color: "#0e7cde",
        stroke_width: 10,
        is_draggable: true,
        points: null,
        name: "",
        width: 1,
        height: 1,
        priority: priority,
        x: posX,
        y: posY,
    }
}

export default function getShape(type: string, imageFormat: SizeType, priority: number = 1, source: string = '') {
    switch (type) {
        case CIRCLE_TYPE:
            return getCircle(imageFormat)
        case CIRCLE_TEXT_TYPE:
            return getCircleText(imageFormat)
        case BRUSH_TYPE:
            return getBrush(imageFormat)
        case RECTANGLE_TYPE:
            return getRectangle(imageFormat)
        case FRAME_TYPE:
            return getFrame(imageFormat, false)
        case DASHED_FRAME_TYPE:
            return getFrame(imageFormat, true)
        case TRIANGLE1_TYPE:
            return getTriangle(imageFormat, type)
        case TRIANGLE2_TYPE:
            return getTriangle(imageFormat, type)
        case BITMAP_TYPE:
            return getBitMap(imageFormat)
        case POLYGON_TYPE:
            return getPolygon(imageFormat)
        case BEZIER_TYPE:
            return getBezier(imageFormat)
        case BADGE_TYPE:
            return getBadge(imageFormat)
        case TEXT_TYPE:
            return getText(imageFormat)
        case DYNAMIC_TEXT_TYPE:
            return getDynamicText(imageFormat)
        case IMAGE_FROM_ELEMENTS_TYPE:
            return getImageFromElements(imageFormat)
        case TEXT_WITH_ELEMENTS_TYPE:
            return getTextWithElements(imageFormat)
        case SOURCE_IMAGE_TYPE:
            return getSourceImage()
        case CUSTOM_SET_TYPE:
            return getCustomSet(imageFormat)
        case CURVED_TEXT_TYPE:
            return getCurvedText(imageFormat)
        case TEXT_INSIDE_SHAPE_TYPE:
            return getTextInsideShape(imageFormat)
        case LINE_TYPE:
            return getLine(imageFormat)
        case EXTERNAL_IMAGE_SOURCE_TYPE:
            return getExternalImageSource(imageFormat)
        case RATING_TYPE:
            return getRating(imageFormat)
        default:
            break;
    }
}

export const DEFAULT_ASPECT_RATIO_TYPES = [CIRCLE_TYPE, TRIANGLE1_TYPE, TRIANGLE2_TYPE, BADGE_TYPE, BITMAP_TYPE, BRUSH_TYPE]
export const EXCLUDE_ASPECT_SIZE_RATIO_TYPES = [FRAME_TYPE, DASHED_FRAME_TYPE, POLYGON_TYPE, BEZIER_TYPE, TEXT_INSIDE_SHAPE_TYPE, LINE_TYPE]
export const EXCLUDE_CURSOR_FOR_TEXT_TYPES = [DYNAMIC_TEXT_TYPE, IMAGE_FROM_ELEMENTS_TYPE]

export function shouldAspectRatio(type: string): boolean {
    return ([TRIANGLE1_TYPE, TRIANGLE2_TYPE, CIRCLE_TYPE].includes(type))
}

export function isTextShapeType(type: string): boolean {
    return ([CIRCLE_TYPE, RECTANGLE_TYPE, TRIANGLE1_TYPE, TRIANGLE2_TYPE, TEXT_TYPE, DYNAMIC_TEXT_TYPE, TEXT_WITH_ELEMENTS_TYPE, CIRCLE_TEXT_TYPE, TEXT_INSIDE_SHAPE_TYPE].includes(type))
}

export function isPointShapeType(type: string): boolean {
    return ([BEZIER_TYPE, POLYGON_TYPE, CURVED_TEXT_TYPE, LINE_TYPE, TEXT_INSIDE_SHAPE_TYPE].includes(type))
}